import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Building2, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "./ui/button";

const REMINDER_DISMISS_KEY = "connect_bank_reminder_dismissed";
const REMINDER_INTERVAL_HOURS = 24; // Show again after 24 hours

export const ConnectBankReminder = ({ plaidConnected, onboardingSkipped }) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only show if user skipped onboarding and hasn't connected a bank
    if (plaidConnected || !onboardingSkipped) {
      setVisible(false);
      return;
    }

    // Check if reminder was dismissed recently
    const dismissedAt = localStorage.getItem(REMINDER_DISMISS_KEY);
    if (dismissedAt) {
      const dismissedTime = new Date(dismissedAt).getTime();
      const now = Date.now();
      const hoursSinceDismissed = (now - dismissedTime) / (1000 * 60 * 60);
      
      if (hoursSinceDismissed < REMINDER_INTERVAL_HOURS) {
        setVisible(false);
        return;
      }
    }

    // Show the reminder
    setVisible(true);
  }, [plaidConnected, onboardingSkipped]);

  const handleDismiss = () => {
    localStorage.setItem(REMINDER_DISMISS_KEY, new Date().toISOString());
    setVisible(false);
  };

  const handleConnect = () => {
    // Navigate to settings and scroll to connected accounts section
    navigate("/settings#accounts");
  };

  if (!visible) return null;

  return (
    <div 
      className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-b border-primary/20"
      data-testid="connect-bank-reminder"
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="hidden sm:flex h-10 w-10 rounded-xl bg-primary/10 items-center justify-center flex-shrink-0">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium text-sm truncate">
                  Connect your bank for real-time insights
                </p>
                <Sparkles className="h-4 w-4 text-amber-500 flex-shrink-0 hidden sm:block" />
              </div>
              <p className="text-xs text-muted-foreground truncate">
                See your actual balances, transactions, and personalized budgets
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={handleConnect}
              size="sm"
              className="gap-1.5 rounded-full"
              data-testid="connect-bank-reminder-btn"
            >
              <span className="hidden sm:inline">Connect Bank</span>
              <span className="sm:hidden">Connect</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground"
              data-testid="dismiss-reminder-btn"
              aria-label="Dismiss reminder"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
